<template>
  <div class="modulos">
    <!-- Loading state del control de acceso -->
    <div v-if="accessLoading" class="d-flex justify-content-center align-items-center" style="min-height: 200px;">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Verificando acceso...</span>
      </div>
    </div>
    
    <!-- Contenido principal cuando se tiene acceso -->
    <div v-else-if="canAccess" class="modulos-content">
      <!-- Toolbar Component inmediatamente después del title bar -->
      <div class="toolbar-container" >
        <ToolbarComponent
          :buttonGroups="[
            {
              id: 'main-actions',
              buttons: [
                {
                  id: 'nuevo',
                  text: 'Nuevo',
                  icon: 'bi bi-plus',
                  variant: 'primary',
                  event: 'new'
                },
                {
                  id: 'actualizar',
                  text: 'Actualizar',
                  icon: 'bi bi-arrow-clockwise',
                  variant: 'secondary',
                  event: 'refresh'
                }
              ]
            }
          ]"
          :rightInfo="`Total: ${modulos.length} módulos`"
          @new="addModulo"
          @refresh="loadModulos"
        />
      </div>
      
      <!-- Contenido de la página con margen para el toolbar fijo -->
      <div class="page-content">
        <div class="alert alert-info" v-if="loading">Cargando módulos...</div>
        <div class="alert alert-danger" v-if="error">{{ error }}</div>
        
        <div class="alert alert-info" v-if="!loading && !error && (!modulos || modulos.length === 0)">
          No hay módulos registrados. Haga clic en 'Nuevo Módulo' para agregar uno.
        </div>

        <!-- Tabla con MyDataGrid -->
        <div v-if="!loading && !error && modulos && modulos.length > 0">
        
        <MyDataGrid
          :columns="dataGridColumns"
          :rows="modulos"
          :enable-pagination="true"
          :per-page="10"
          :page-sizes="[5, 10, 25, 50]"
          filter-placeholder="Buscar módulos..."
          :striped="true"
          :compact="true"
          :show-actions="true"
        >
          <!-- Slot personalizado para la columna EsActivo -->
          <template #cell-EsActivo="{ value }">
            <span v-if="value" class="badge bg-success">Sí</span>
            <span v-else class="badge bg-secondary">No</span>
          </template>
          
          <!-- Slot personalizado para las rutas -->
          <template #cell-Ruta="{ value }">
            <code>{{ value }}</code>
          </template>
          
          <template #cell-RutaApi="{ value }">
            <code>{{ value }}</code>
          </template>
          
          <!-- Slot de acciones personalizadas -->
           <template #actions="{ item }">
            <button 
              @click="editModulo(item)" 
              class="btn btn-sm btn-outline-primary me-1"
              title="Editar módulo"
            >
              <i class="bi bi-pencil"></i>
            </button>
            <button 
              @click="confirmDelete(item.ModuloCod)" 
              class="btn btn-sm btn-outline-danger"
              title="Eliminar módulo"
            >
              <i class="bi bi-trash"></i>
            </button>
          </template>
        </MyDataGrid>
        </div>

        <div class="mt-4">
          <router-link to="/" class="btn btn-secondary">
            <i class="bi bi-arrow-left me-1"></i> Volver al Menú
          </router-link>
        </div>
      </div>
    </div>
    
    <!-- Mensaje cuando no se tiene acceso -->
    <div v-else class="alert alert-warning text-center">
      <h4>Acceso Restringido</h4>
      <p>No tiene permisos para acceder a esta página.</p>
      <router-link to="/" class="btn btn-primary">Volver al Inicio</router-link>
    </div>

    <!-- Modal para Nuevo Módulo -->
    <div 
      class="modal fade" 
      :class="{ 'show d-block': showModal }"
      id="nuevoModuloModal" 
      tabindex="-1" 
      aria-labelledby="nuevoModuloModalLabel" 
      :aria-hidden="!showModal"
      @click.self="closeModal"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="nuevoModuloModalLabel">
              <i class="bi bi-plus-circle me-2"></i>Nuevo Módulo
            </h5>
            <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <ModuloForm
              :modulo="formData"
              :isEditing="false"
              :loading="modalLoading"
              @submit="handleSubmit"
              @cancel="closeModal"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAccessControl } from '@/composables/useAccessControl'
import ModulosViewController from '../controllers/ModulosViewController';
import MyDataGrid from '@/shared/components/MyDataGrid.vue';
import ToolbarComponent from '@/shared/components/ToolbarComponent.vue';
import ModuloForm from '../components/ModuloForm.vue';

export default {
  name: 'ModulosView',
  components: {
    MyDataGrid,
    ToolbarComponent,
    ModuloForm
  },
  setup() {
    const { 
      requiresAuth, 
      requiresMenuPermission, 
      canAccess, 
      isLoading: accessLoading 
    } = useAccessControl({
      requiresAuth: true,
      requiresMenuPermission: false
    })

    return {
      requiresAuth,
      requiresMenuPermission,
      canAccess,
      accessLoading
    }
  },
  data() {
    return {
      controller: new ModulosViewController(),
      // Modal state
      showModal: false,
      modalLoading: false,
      formData: {
        Modulo_cod: '',
        Nombre: '',
        Ruta: '',
        RutaApi: '',
        Permisos: 0,
        Es_activo: true
      }
    };
  },
  computed: {
    modulos() {
      return this.controller.modulos;
    },
    loading() {
      return this.controller.loading;
    },
    error() {
      return this.controller.error;
    },
    // Configuración de columnas para MyDataGrid
    dataGridColumns() {
      return [
        { key: 'ModuloCod', label: 'Código', sortable: true },
        { key: 'Nombre', label: 'Nombre', sortable: true },
        { 
          key: 'EsActivo', 
          label: 'Activo', 
          sortable: true,
          format: (value) => value ? 'Sí' : 'No'
        },
        { key: 'Permisos', label: 'Permisos', sortable: true },
        { key: 'Ruta', label: 'Ruta', sortable: true },
        { key: 'RutaApi', label: 'Ruta API', sortable: true },
        { key: 'Sistema', label: 'Sistema', sortable: true }
      ];
    },
    // Verificar si el usuario puede crear módulos
    canCreate() {
      // Por ahora retornamos true, pero aquí se puede implementar lógica de permisos
      return this.canAccess;
    }
  },
  created() {
    this.controller.initialize();
  },
  mounted() {
    // Emitir evento al padre para notificar que esta página tiene buttonbar
    this.$emit('toolbar-mounted');
  },
  methods: {
    async fetchModulos() {
      await this.controller.loadModulos();
    },
    async loadModulos() {
      console.log("cargando modulos ... **** ");
      await this.controller.loadModulos();
    },
    addModulo() {
      this.openModal();
    },
    editModulo(modulo) {
      this.$router.push(`/modulos/edit/${modulo.ModuloCod}`);
    },
    confirmDelete(moduloCod) {
      console.log('Iniciando confirmación de eliminación para:', moduloCod);
      
      // Usar SweetAlert para la confirmación
      this.$alert.confirm(
        `¿Está seguro que desea eliminar el módulo con código "${moduloCod}"?`,
        'Confirmar eliminación',
        { icon: 'warning' }
      ).then((result) => {
        console.log('Resultado de confirmación:', result);
        
        if (result.isConfirmed) {
          // Si el usuario confirmó, proceder con la eliminación
          this.deleteModulo(moduloCod);
        }
      });
    },
    async deleteModulo(moduloCod) {
      console.log('Ejecutando eliminación para:', moduloCod);
      
      try {
        const response = await this.controller.deleteModulo(moduloCod);
        console.log('Respuesta de eliminación recibida:', response);
        
        // Usar el manejador de respuestas MgkMessage
        this.$mgkResponse.handleResponse(response).then(() => {
          console.log('Recargando lista de módulos después de eliminación');
          // Recargar la lista de módulos después de mostrar el mensaje
          this.controller.reloadModulos();
        });
      } catch (error) {
        console.error('Error al eliminar módulo:', error);
        this.$alert.error('No se pudo eliminar el módulo. Por favor, intente nuevamente.');
      }
    },
    // Métodos del modal
    openModal() {
      this.resetForm();
      this.showModal = true;
      // Agregar clase al body para evitar scroll
      document.body.classList.add('modal-open');
    },
    closeModal() {
      this.showModal = false;
      this.modalLoading = false;
      this.resetForm();
      // Remover clase del body
      document.body.classList.remove('modal-open');
    },
    resetForm() {
      this.formData = {
        Modulo_cod: '',
        Nombre: '',
        Ruta: '',
        RutaApi: '',
        Permisos: 0,
        Es_activo: true
      };
    },
    async handleSubmit(formData) {
      this.modalLoading = true;
      try {
        await this.controller.createModulo(formData);
        this.closeModal();
        this.loadModulos(); // Recargar la lista
        // Mostrar mensaje de éxito si tienes un sistema de notificaciones
        console.log('Módulo creado exitosamente');
      } catch (error) {
        console.error('Error al crear módulo:', error);
        // Aquí podrías mostrar un mensaje de error al usuario
      } finally {
        this.modalLoading = false;
      }
    }
  }
}
</script>

<style scoped>
.modulos {
  min-height: calc(100vh - 200px);
}

.modulos-content {
  padding: 0;
}

.page-header {
  padding: 1rem 0 0.5rem 0;
  border-bottom: 1px solid #e9ecef;
  background-color: #f8f9fa;
  margin: -1rem -1rem 0 -1rem;
  padding-left: 1rem;
  padding-right: 1rem;
}

.page-header h1 {
  color: #495057;
  font-size: 1.75rem;
  font-weight: 600;
}

.page-header p {
  font-size: 0.95rem;
}


/* Ensure proper spacing with fixed title bar */

/* Modal styles */
.modal.show {
  display: block !important;
}



</style>
